import type {
  GenericError,
  SuccessApiResponse,
  SignUpResponse,
  UserDetails,
  AgencyList,
} from 'src/types/referrer-portal';
import * as sentry from 'sentry';
import type {Dispatch, ThunkAction} from 'src/reducers';
import {ERROR_CODE, DEFAULT_API_ERROR} from 'src/utils/referrer-portal';
import {camel} from 'src/utils';
import urlParse from 'url-parse';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import * as reduxApi from 'src/utils/redux-api-referrer-portal';
import {AnalyticsService} from 'src/analytics';
import {unregisterAllServiceWorkers} from 'src/utils/service-workers';
import IndexStore from 'src/stores/index';
import logger from 'src/utils/logger';
import {getValidNextPath} from 'src/utils/security';
import {toastMessage} from 'src/components/referral-v2/tracking/referral-confirm-modal';
import {toastApi} from '@spaced-out/ui-design-system/lib/components/Toast';


export const USER_SIGNIN = 'user/signin';
export const USER_SIGNIN_ERROR = 'user/signin-error';

export const USER_SIGNUP = 'user/signup';
export const USER_SIGNUP_ERROR = 'user/signup-error';

export const USER_RESET_PASSWORD = 'user/reset-password';
export const USER_RESET_PASSWORD_ERROR = 'user/reset-password-error';

export const USER_AUTH = 'user/auth';

export const RECEIVE_AGENCY = 'receive/agency';
export const RECEIVE_AGENCY_ERROR = 'receive/agency-error';

export const RECEIVE_USER_DETAILS = 'receive/user-details';
export const RECEIVE_USER_DETAILS_ERROR = 'receive/user-details-error';

export const RECEIVE_NOTIFICATION = 'receive/notification';
export const RECEIVE_NOTIFICATION_ERROR = 'receive/notification-error';

type UserSigninAction = {
  type: typeof USER_SIGNIN;
  payload: SuccessApiResponse;
};

type UserSigninErrorAction = {
  type: typeof USER_SIGNIN_ERROR;
  payload?: string | null | undefined;
};

type UserSignUpAction = {
  type: typeof USER_SIGNUP;
  payload: SignUpResponse;
};

type UserSignUpErrorAction = {
  type: typeof USER_SIGNUP_ERROR;
  payload?: string | null | undefined;
};

type UserResetPasswordAction = {
  type: typeof USER_RESET_PASSWORD;
  payload: SuccessApiResponse;
};

type UserResetPasswordErrorAction = {
  type: typeof USER_RESET_PASSWORD_ERROR;
  payload?: string | null | undefined;
};

type UserAuthAction = {
  type: typeof USER_AUTH;
  payload: boolean;
};

type AgencyAction = {
  type: typeof RECEIVE_AGENCY;
  payload: AgencyList[];
};

type AgencyErrorAction = {
  type: typeof RECEIVE_AGENCY_ERROR;
  payload?: GenericError | null | undefined;
};

type UserDetailsAction = {
  type: typeof RECEIVE_USER_DETAILS;
  payload: UserDetails;
};

type UserDetailsErrorAction = {
  type: typeof RECEIVE_USER_DETAILS_ERROR;
  payload?: GenericError | null | undefined;
};

type NotificationAction = {
  type: typeof RECEIVE_NOTIFICATION;
  payload: {
    [key: string]: boolean;
  };
};

type NotificationErrorAction = {
  type: typeof RECEIVE_NOTIFICATION_ERROR;
  payload?: GenericError | null | undefined;
};

export type AuthenticationActions =
  | UserSigninAction
  | UserSigninErrorAction
  | UserSignUpAction
  | UserSignUpErrorAction
  | UserResetPasswordAction
  | UserResetPasswordErrorAction
  | UserAuthAction
  | AgencyAction
  | AgencyErrorAction
  | UserDetailsAction
  | UserDetailsErrorAction
  | NotificationAction
  | NotificationErrorAction;

export const getAgencyError = (payload: GenericError): AgencyErrorAction => ({
  type: RECEIVE_AGENCY_ERROR,
  payload,
});

export const getUserDetailsError = (
  payload: GenericError,
): UserDetailsErrorAction => ({
  type: RECEIVE_USER_DETAILS_ERROR,
  payload,
});

export const getNotificationsError = (
  payload: GenericError,
): NotificationErrorAction => ({
  type: RECEIVE_NOTIFICATION_ERROR,
  payload,
});

export const userSignin: (payload: {
  email: string;
  password: string;
}) => ThunkAction<void> = flow(
  key(() => `auth/login`),
  fetching(),
)((payload) => async (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`auth/login`, payload)).then(
    (response: SuccessApiResponse) => {
      dispatch(performSignIn());
      return dispatch({type: USER_SIGNIN, payload: response});
    },
    (error) => {
      const errorObject = JSON.parse(error.response);
      const apiError: string = errorObject.errors[0]
        ? errorObject.errors[0]
        : 'Incorrect Email and Password';
      dispatch({
        type: USER_SIGNIN_ERROR,
        payload: apiError,
      });
    },
  );
});

export const userSignUp: (payload: {
  email: string;
  password: string;
  name: string;
}) => ThunkAction<void> = flow(
  key(() => `auth/signup`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`auth/signup`, payload)).then(
    (response: SignUpResponse) => {
      dispatch({type: USER_SIGNUP, payload: response});
      dispatch(performSignIn(true));
    },
    (error) => {
      let apiError: string =
        'Failed to perform action, please try after sometime';
      if (error?.responseBody?.errors && error.responseBody.errors[0]) {
        apiError = error.responseBody.errors[0];
      }
      dispatch({
        type: USER_SIGNUP_ERROR,
        payload: apiError,
      });
    },
  );
});

export const userResetPassword: (payload: {
  reset_id: string;
  new_password: string;
  new_password_copy: string;
}) => ThunkAction<unknown> = flow(
  key(() => `auth/reset-password`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`auth/reset-password`, payload)).then(
    (response: SuccessApiResponse) => {
      dispatch({type: USER_RESET_PASSWORD, payload: response});
      return response;
    },
    (error) => {
      let apiError: string =
        'Failed to perform action, please try after sometime';
      if (
        error?.responseBody?.errors &&
        typeof error.responseBody.errors[0] === 'string'
      ) {
        apiError = error.responseBody.errors[0];
      }
      dispatch({
        type: USER_RESET_PASSWORD_ERROR,
        payload: apiError,
      });
    },
  );
});

export const getAllowedAgency: () => ThunkAction<void> = flow(
  key(() => `auth/agency/list`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`auth/agency/list`)).then(
    (response: AgencyList[]) => {
      dispatch({type: RECEIVE_AGENCY, payload: response});
    },
    (error) => {
      dispatch(getAgencyError(error?.response?.errors || DEFAULT_API_ERROR));
    },
  );
});

export const getUserDetails: (store: IndexStore) => ThunkAction<void> = flow(
  key(() => `user`),
  fetching(),
)(
  (store) => (dispatch: Dispatch) =>
    dispatch(reduxApi.get(`user`)).then(
      (response: UserDetails) => {
        const user = camel(response);
        store?.me?.receiveFetched(user);
        dispatch({type: RECEIVE_USER_DETAILS, payload: response});
        dispatch({type: USER_AUTH, payload: true});
      },
      (error) => {
        dispatch(
          getUserDetailsError(error?.response?.errors || DEFAULT_API_ERROR),
        );
        store?.me?.receiveFetched(null);
      },
    ),
);

export const getNotification: () => ThunkAction<void> = flow(
  key(() => `user/nofitication-settings`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`user/nofitication-settings`)).then(
    (response: {[key: string]: boolean}) => {
      dispatch({type: RECEIVE_NOTIFICATION, payload: response});
    },
    (error) => {
      dispatch(
        getNotificationsError(error?.response?.errors || DEFAULT_API_ERROR),
      );
    },
  );
});

export const getAutoSubmitNotification: () => ThunkAction<{
  [key: string]: boolean;
}> = flow(
  key(() => `user/auto-submit-lead`),
  fetching(),
)(() => (dispatch: Dispatch) => {
  return dispatch(reduxApi.get(`user/auto-submit-lead`)).then(
    (response: {[key: string]: boolean}) => {
      return response;
    },
  );
});

export const updateNotification: (payload: {
  settings: {
    [key: string]: boolean;
  };
}) => ThunkAction<void> = flow(
  key(() => `user/nofitication-settings`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`user/nofitication-settings`, payload)).then(
    (response: {[key: string]: boolean}) => {
      dispatch({type: RECEIVE_NOTIFICATION, payload: response});
      toastApi.show(
        toastMessage({
          message: 'Notification Updated Successfully',
        }),
      );
    },
    (error) => {
      toastApi.show(
        toastMessage({
          message: error?.responseBody?.message,
          semantic: 'danger',
        }),
      );
    },
  );
});

// @ts-expect-error - TS2322 - Type '(payload: { [key: string]: boolean; }) => (dispatch: Dispatch) => Promise<void | { [key: string]: boolean; }>' is not assignable to type '(payload: { [key: string]: boolean; }) => ThunkAction<{ [key: string]: boolean; } | null | undefined>'.
export const updateAutoSubmitNotification: (payload: {
  [key: string]: boolean;
}) => ThunkAction<
  | {
      [key: string]: boolean;
    }
  | null
  | undefined
> = flow(
  key(() => `user/auto-submit-lead`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.put(`user/auto-submit-lead`, payload)).then(
    (response: {[key: string]: boolean}) => {
      toastApi.show(
        toastMessage({
          message: 'Notification Updated Successfully',
        }),
      );
      return response;
    },
    (error) => {
      toastApi.show(
        toastMessage({
          message: error?.responseBody?.message,
          semantic: 'danger',
        }),
      );
    },
  );
});

export const createPaymentDetails: (payload: {
  [key: string]:
    | boolean
    | string
    | {
        [key: string]: string;
      };
}) => ThunkAction<unknown> = flow(
  key(() => `user/payment-details`),
  fetching(),
)((payload) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post('user/payment-details', payload))
    .then((response) => {
      if (response) {
        toastApi.show(
          toastMessage({
            message: 'Payment details updated successfully',
          }),
        );
        return response;
      }
    })
    .catch((error) => {
      const apiError = error?.responseBody?.errors[0];
      const defaultError = 'Payment details not updated';
      toastApi.show(
        toastMessage({
          message:
            typeof apiError === 'string'
              ? // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly PaymentMethodAlreadyExists: "Payment Method Already Exists"; readonly ServerError: "Internal Server Error"; }'.
                ERROR_CODE[apiError] ?? defaultError
              : defaultError,
          semantic: 'danger',
        }),
      );
    });
});

export const signOut: (store: IndexStore) => ThunkAction<void> = flow(
  key(() => `auth/logout`),
  fetching(),
)((store) => async (dispatch: Dispatch) => {
  unregisterAllServiceWorkers();
  store.me.clearAuth();
  const scope = sentry.getCurrentScope();
  scope.clear();
  trackLogin('sign-out', {isSuccees: true});
  try {
    const response = await dispatch(reduxApi.del('auth/logout'));
    // Check if the response contains a logout_redirect_url
    if (response && response.logout_redirect_url) {
      // Redirect the user to the specified URL from backend
      window.location.href = response.logout_redirect_url;
    } else {
      // Default redirect to signin if no redirect URL provided
      // @ts-expect-error - TS2339 - Property 'history' does not exist on type 'IndexStore'.
      store.history.replace('/signin');
    }
    dispatch({type: USER_AUTH, payload: false});
  } catch {}
});

export const performSignIn: (fromSignup?: boolean) => ThunkAction<unknown> =
  (fromSignup = false) =>
  (): Promise<boolean> => {
    return new Promise(
      async (resolve: (result: Promise<boolean> | boolean) => void) => {
        try {
          const {host, protocol} = window.location;
          const baseDomain = host.substring(host.indexOf('.') + 1);
          // @ts-expect-error - TS2769 - No overload matches this call.
          const query = urlParse(window.location, true).query;
          const slug = 'referrals';
          const nextPath = fromSignup
            ? '/dashboard/setup'
            : query.nextPathname
            ? query.nextPathname
            : '';
          const validNextPath = getValidNextPath(nextPath);
          const nextSearch = query.nextSearch || '';
          trackLogin('sign-in', {isSuccees: true});
          // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'string & Location'.
          window.location = `${protocol}//${slug}.${baseDomain}${validNextPath}${nextSearch}`;
          resolve(true);
        } catch (error: any) {
          trackLogin('sign-in', {
            isSuccees: false,
            error: JSON.stringify(error),
          });
          logger.error(error);
        }
      },
    );
  };

function trackLogin(
  event: string,
  props:
    | {
        error: string;
        isSuccees: boolean;
      }
    | {
        isSuccees: boolean;
      },
) {
  try {
    AnalyticsService.track(event, props);
  } catch (error: any) {
    console.log('error: ', error);
  }
}
